import { get, groupBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ActionType } from 'react-table';

import { SEARCH_RESULTS_LIMIT } from '~/common/constants/max_lengths';
import { NAV_AND_SUBNAV_HEIGHT } from '~/common/constants/page';
import { getSearchConstants } from '~/common/constants/search';
import { ExportExcel } from '~/components/Shared/ExportExcel/ExportExcel';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Table } from '~/eds/src/modules/Table/Table';
import { FieldId } from '~/evifields/src/types/FieldId';
import { SearchQuery } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Dashboard } from '~/redux/api/methods/dashboardV2';
import {
  getFieldValue,
  SearchItem,
  SearchResult,
} from '~/redux/api/methods/evisearch';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { SearchFilter } from '~/types/DashboardV2';
import { getMaximumByLimit } from '~/utils/number';

import { FIRST_COLUMN_PREFIX, getFirstColumnKey } from '../utils';

type Props = {
  dashboard?: Dashboard;
  isLoading: boolean;
  data?: SearchResult;
  query: SearchQuery;
  searchFilters: Record<FieldId, SearchFilter>;
};

export const DashboardV3SearchResults = ({
  dashboard,
  isLoading,
  data,
  query,
  searchFilters,
}: Props) => {
  const intl = useIntl();
  const hasLocalization = useFlag(FlagType.LanguageSelect);
  const page = useSelector(slices.dashboardV2.selectors.selectPage);
  const pageSize = useSelector(slices.dashboardV2.selectors.selectPageSize);
  const sortBy = useSelector(slices.dashboardV2.selectors.selectSortBy);
  const columnOrder = useSelector(
    slices.dashboardV2.selectors.selectColumnOrder,
  );
  const columnWidths = useSelector(
    slices.dashboardV2.selectors.selectColumnWidths,
  );
  const dispatch = useDispatch();
  const { EXPORT_DEFAULT_TEXT } = getSearchConstants(intl);

  const [showExportExcel, setShowExportExcel] = useState(false);

  const onSetColumnOrder = useCallback(
    (updatedColumnOrder: Array<string>) => {
      dispatch(slices.dashboardV2.actions.setColumnOrder(updatedColumnOrder));
      dispatch(slices.dashboardV2.actions.setDashboardDirty(true));
    },
    [searchFilters],
  );

  const onPaginate = useCallback(
    ({ pageIndex }: { pageIndex: number }) => {
      if (pageIndex !== page) {
        dispatch(slices.dashboardV2.actions.setPage(pageIndex));
      }
    },
    [page],
  );

  const onPageSizeChange = useCallback((pageSize: number) => {
    dispatch(slices.dashboardV2.actions.setPageSize(pageSize));
  }, []);

  // Using type any since there no interface for inner table state.
  const handleUpdate = (state: any, action?: ActionType) => {
    if (action?.type === 'columnDoneResizing') {
      dispatch(
        slices.dashboardV2.actions.patchColumnWidths(
          state.columnResizing.columnWidths,
        ),
      );
    }
    if (action?.type === 'toggleSortBy') {
      dispatch(slices.dashboardV2.actions.setSortBy(state.sortBy[0]));
    }
  };

  /*
   * Columns
   */
  const firstColumnName = getFirstColumnKey(dashboard?.entity ?? '');
  const hardcodedColumns = [
    {
      key: firstColumnName,
      title: intl.formatMessage({
        id: 'DashboardV3SearchResults.nameColumn',
        defaultMessage: 'Name',
        description: 'Column header for name field',
      }),
      minWidth: 'm',
      cellType: 'link',
      mapCellProps: (item: SearchItem) => {
        let pathname;
        if (item.type === 'ticket') {
          pathname = RoutePathType.WorkflowTicketsTicket.replace(
            ':ticketId',
            item.id,
          );
        }
        return {
          text: get(item, `selected_field_values.${firstColumnName}.value[0]`),
          pathname,
          state: { isRedirect: true },
        };
      },
      rawWidth: columnWidths[firstColumnName],
    },
  ];

  const actions = useMemo(
    () => [
      {
        icon: 'file-spreadsheet',
        label: EXPORT_DEFAULT_TEXT,
        onClick: () => {
          setShowExportExcel(true);
        },
      },
    ],
    [],
  );

  const columns = useMemo(() => {
    return [
      ...hardcodedColumns,
      ...Object.entries(searchFilters)
        .filter(([_id, filter]) => !filter.id.startsWith(FIRST_COLUMN_PREFIX))
        .map(([id, filter]) => ({
          ...mapFilterToColumn(id, filter, hasLocalization),
          rawWidth: columnWidths[id],
        })),
    ];
  }, [searchFilters, columnWidths, columnOrder]);

  const sectionsResult = api.endpoints.getFilterSections.useQuery(undefined, {
    skip: dashboard?.entity === 'ticket',
  });

  const { data: filterSections } = sectionsResult;

  const columnsMap = groupBy(columns, 'section');

  const columnGroups = useMemo(() => {
    if (filterSections?.fieldGroups) {
      return filterSections?.fieldGroups
        .map((group) => ({
          name: group.id,
          label: group.label,
          columns: columnsMap[group.id]?.map((c) => c.key),
        }))
        .filter((group) => group.columns?.length);
    }
    return undefined;
  }, [columnsMap, filterSections]);

  /*
   * Columns End
   */
  const placeholderContent =
    data && !data.results?.length
      ? {
          title: intl.formatMessage({
            id: 'DashboardV3SearchResults.noResults',
            defaultMessage: 'No results found',
            description: 'Message when no search results are available',
          }),
        }
      : undefined;

  const TableComponent = dashboard?.is_default ? Table : PersistedTable;

  return (
    <ContentContainer placeholderContent={placeholderContent}>
      <Layout direction="column" spacing={8}>
        <TableComponent
          actions={actions}
          context={`DASHBOARD:${dashboard?.id}`}
          name={intl.formatMessage({
            id: 'DashboardV3SearchResults.searchResultTableName',
            defaultMessage: 'search result',
            description: 'Name for the search results table',
          })}
          totalCount={getMaximumByLimit(
            data?.meta?.total,
            SEARCH_RESULTS_LIMIT,
          )}
          columns={columns}
          columnGroups={columnGroups}
          data={data?.results}
          isLoading={isLoading}
          state={{
            columnOrder: columnOrder,
            pageIndex: page,
            pageSize,
            sortBy: [sortBy],
          }}
          onPaginate={onPaginate}
          onPageSizeChange={onPageSizeChange}
          onSetColumnOrder={onSetColumnOrder}
          onUpdate={handleUpdate}
          options={{
            enableExportXlsx: false,
            enablePageSizeSelect: true,
            enableSelectRows: false,
            enableStickyHeader: true,
            stickyHeaderOffset: NAV_AND_SUBNAV_HEIGHT,
          }}
          reactTableOptions={{
            autoResetSortBy: false,
            disableSortRemove: true,
            manualSortBy: true,
          }}
        />

        <ExportExcel
          entity={dashboard?.entity}
          query={query}
          isVisible={showExportExcel}
          onHide={() => setShowExportExcel(false)}
          totalDocs={data?.meta?.total}
          totalDupsAndDocs={data?.meta?.total}
          selectedFieldIds={columnOrder}
        />
      </Layout>
    </ContentContainer>
  );
};

function mapFilterToColumn(
  id: FieldId,
  searchFilter: SearchFilter,
  hasLocalization: boolean,
) {
  const mapToCellProps = (filter: SearchItem) => {
    const value = filter.selected_field_values[id];
    if (searchFilter.type === 'date') {
      const dateValue = new Date(get(value, 'value[0]'));
      return {
        datetime: dateValue,
        format: hasLocalization ? 'short' : 'iso',
      };
    }

    return {
      text: getFieldValue(value?.value),
    };
  };

  const getTableCellType = () => {
    switch (searchFilter.type) {
      case 'date':
        return 'datetime';
      case 'text':
        return 'text';
      default:
        return 'text';
    }
  };

  return {
    key: id,
    field: searchFilter,
    title: searchFilter.label,
    cellType: getTableCellType(),
    mapCellProps: mapToCellProps,
    section: searchFilter.section,
  };
}
